/**
 * @typedef {import('openapi-fetch').Middleware} Middleware
 * @typedef {{platform: string, appVersion: string}} ContextConfig
 */

/**
 * Per-call options live on the Request object itself (openapi-fetch attaches unrecognized `init`
 * keys to it), so a rebuilt Request must carry them over.
 * `idempotencyKey` is sent as `X-BKFC-Request-Id` instead of a fresh id (reuse it across retries
 * of one logical operation); `noAuth` skips the Authorization header.
 */
const CUSTOM_OPTION_KEYS = ['idempotencyKey', 'noAuth'];

/** @returns {{idempotencyKey?: string, noAuth?: boolean}} */
export const readCustomOptions = request => request;

export function carryCustomOptions(from, to) {
  CUSTOM_OPTION_KEYS.forEach(key => {
    if (from[key] !== undefined) {
      to[key] = from[key];
    }
  });
  return to;
}

/**
 * Injects request context onto every call: `platform` as a query param (the server keys the edge
 * cache on it) and the version / request-id headers.
 * @param {ContextConfig} config
 * @returns {Middleware}
 */
export function createContextMiddleware(config) {
  return {
    async onRequest({ request }) {
      const url = new URL(request.url);
      url.searchParams.set('platform', config.platform);
      const headers = new Headers(request.headers);
      headers.set('X-BKFC-App-Version', config.appVersion);
      headers.set(
        'X-BKFC-Request-Id',
        readCustomOptions(request).idempotencyKey || globalThis.crypto.randomUUID(),
      );
      // A new URL needs a new Request. Copy method/body explicitly: spreading a Request copies
      // nothing (they are prototype getters), which would turn every POST into a body-less GET.
      const hasBody = request.method !== 'GET' && request.method !== 'HEAD';
      return carryCustomOptions(
        request,
        new Request(url, {
          method: request.method,
          headers,
          body: hasBody ? await request.arrayBuffer() : undefined,
          signal: request.signal,
          redirect: request.redirect,
          credentials: request.credentials,
          // A release or a last progress write on page hide must outlive the page.
          keepalive: request.keepalive,
        }),
      );
    },
  };
}
